import { useEffect, useMemo, useState, type FormEvent } from 'react';
import { inputStyle, PremiumField } from '@kilat/ui';
import { Link, useNavigate } from 'react-router-dom';
import { Timestamp } from 'firebase/firestore';

import { useAuth } from '../lib/use-auth.js';
import { useI18n } from '../lib/use-i18n.js';
import { usePlatformBrand } from '../lib/use-platform-brand.js';
import { useActiveOutlet } from '../lib/use-active-outlet.js';
import { tenantsRepo } from '../lib/repositories/tenants-repo.js';
import { brandingRepoFor } from '../lib/repositories/branding-repo.js';
import { outletDocRepoFor } from '../lib/repositories/outlet-repos.js';
import { subscriptionRepoFor } from '../lib/repositories/subscription-repo.js';
import { callBrandingRegenerate } from '../lib/callables/branding.js';
import { logoSourcePath, uploadFile } from '../lib/storage-upload.js';
import { logger } from '../lib/logger.js';
import {
  customerAppUrl,
  customerAppUrlForDisplay,
  type SubscriptionState,
  type TenantProfile,
} from '@kilat/shared';

/**
 * /welcome — onboarding wizard (operator's first impression).
 *
 * Four steps:
 *   1. Confirm your outlet — name + address
 *   2. Business hours — single weekly open/close (per-day in Sprint 10)
 *   3. Logo — upload, regenerate icon set; skippable (operator can do it later
 *      from /branding). Premium-logical: skipping is a real "Skip" button, not
 *      a hidden behaviour, because the operator should know they're shipping
 *      with the placeholder.
 *   4. Welcome card with trial countdown + QR slug + "Open queue"
 *
 * On Finish:
 *   - Writes outlet profile (name, address) + branding.hours via client repos
 *     (owner role on the tenant per claimsSync grants the write per firestore.rules)
 *   - If a logo was uploaded: uploadBytes → callBrandingRegenerate (sharp
 *     generates the icon set + bumps brandingVersion)
 *   - Flips tenants/{tid}.profile.onboardingCompleted = true
 *   - Navigates to /queue
 *
 * If the operator returns to /welcome after completion, we show the post-onboarding
 * "all set" card instead of the wizard.
 */

type WizardStep = 1 | 2 | 3 | 4;

interface DayHours {
  isOpen: boolean;
  open: string;
  close: string;
}

const DAYS = ['sun', 'mon', 'tue', 'wed', 'thu', 'fri', 'sat'] as const;
type DayKey = (typeof DAYS)[number];

export function WelcomePage() {
  const { user, claims, loading } = useAuth();
  const { t } = useI18n();
  const brand = usePlatformBrand();
  const { outletId, loading: outletLoading } = useActiveOutlet();
  const nav = useNavigate();

  const tenantId =
    typeof claims?.['defaultTenant'] === 'string' ? (claims['defaultTenant'] as string) : null;

  const [profile, setProfile] = useState<TenantProfile | null>(null);
  const [subscription, setSubscription] = useState<SubscriptionState | null>(null);
  const [tenantLoaded, setTenantLoaded] = useState(false);

  useEffect(() => {
    if (!tenantId) return;
    let cancelled = false;
    (async () => {
      try {
        const [tenant, sub] = await Promise.all([
          tenantsRepo.get(tenantId),
          subscriptionRepoFor(tenantId).get('current'),
        ]);
        if (cancelled) return;
        setProfile(tenant?.profile ?? null);
        setSubscription(sub);
        setTenantLoaded(true);
      } catch (e) {
        logger.error('welcome: failed to load tenant or subscription', {
          tenantId,
          error: String(e),
        });
        if (!cancelled) setTenantLoaded(true);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [tenantId]);

  if (loading) return <Screen body={t('dashboard.welcome.signingIn')} />;
  if (!user) return <Screen body={t('dashboard.welcome.notSignedIn')} />;
  if (!tenantId) return <Screen body={t('dashboard.welcome.noTenant')} />;
  if (!tenantLoaded || outletLoading)
    return <Screen body={t('dashboard.welcome.loadingCarwash')} />;
  if (!profile) return <Screen body={t('dashboard.welcome.tenantNotFound', { tenantId })} />;
  if (!outletId) return <Screen body={t('dashboard.welcome.noOutlets')} />;

  // Already onboarded? Skip straight to the summary; otherwise run the wizard.
  if (profile.onboardingCompleted === true) {
    return (
      <PostOnboardingSummary
        brand={brand}
        profile={profile}
        subscription={subscription}
        onOpenQueue={() => nav('/queue')}
      />
    );
  }

  return (
    <Wizard
      tenantId={tenantId}
      outletId={outletId}
      brandName={brand.name}
      onDone={() => nav('/queue')}
    />
  );
}

// ── Wizard ──────────────────────────────────────────────────────────────

function Wizard({
  tenantId,
  outletId,
  brandName,
  onDone,
}: {
  readonly tenantId: string;
  readonly outletId: string;
  readonly brandName: string;
  readonly onDone: () => void;
}) {
  const { t } = useI18n();
  const [step, setStep] = useState<WizardStep>(1);

  // Step 1
  const [outletName, setOutletName] = useState('');
  const [address, setAddress] = useState('');

  // Step 2 — single weekly window applied to all open days
  const [openTime, setOpenTime] = useState('09:00');
  const [closeTime, setCloseTime] = useState('18:00');
  const [openDays, setOpenDays] = useState<Set<DayKey>>(new Set(DAYS));

  // Step 3 — logo (optional)
  const [logoFile, setLogoFile] = useState<File | null>(null);
  const [logoPreview, setLogoPreview] = useState<string | null>(null);
  useEffect(() => {
    if (!logoFile) {
      setLogoPreview(null);
      return;
    }
    const url = URL.createObjectURL(logoFile);
    setLogoPreview(url);
    return () => URL.revokeObjectURL(url);
  }, [logoFile]);

  // Prefill from current outlet + branding so the wizard isn't blank
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const outlet = await outletDocRepoFor(tenantId).get(outletId);
        const branding = await brandingRepoFor(tenantId).get('current');
        if (cancelled) return;
        const profile = (outlet?.profile ?? {}) as { name?: string; address?: string };
        if (profile.name) setOutletName(profile.name);
        if (profile.address) setAddress(profile.address);
        const weekly = branding?.hours.weekly;
        if (weekly) {
          const openSet = new Set<DayKey>();
          let inferredOpen = '09:00';
          let inferredClose = '18:00';
          for (const d of DAYS) {
            const h = weekly[d];
            if (h?.isOpen) {
              openSet.add(d);
              if (h.open) inferredOpen = h.open;
              if (h.close) inferredClose = h.close;
            }
          }
          if (openSet.size > 0) setOpenDays(openSet);
          setOpenTime(inferredOpen);
          setCloseTime(inferredClose);
        }
      } catch (e) {
        logger.warn('wizard prefill failed', { error: String(e) });
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [tenantId, outletId]);

  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);

  async function commitAndFinish() {
    setSubmitError(null);
    if (!outletName.trim()) {
      setSubmitError(t('dashboard.welcome.errNoName'));
      setStep(1);
      return;
    }
    setSubmitting(true);
    try {
      const now = Timestamp.now();

      // Build weekly hours from the single window
      const weekly: Record<DayKey, DayHours> = {} as Record<DayKey, DayHours>;
      for (const d of DAYS) {
        const isOpen = openDays.has(d);
        weekly[d] = isOpen
          ? { isOpen: true, open: openTime, close: closeTime }
          : { isOpen: false, open: '', close: '' };
      }

      // Update outlet profile (partial — uses dotted field paths)
      await outletDocRepoFor(tenantId).update(outletId, {
        'profile.name': outletName.trim(),
        'profile.address': address.trim(),
        'profile.updatedAt': now,
      });

      // Update branding hours (partial)
      await brandingRepoFor(tenantId).update('current', {
        'hours.weekly': weekly,
      });

      // Optional logo: upload bytes, then trigger the sharp pipeline.
      // We don't block onboarding on this — if regenerate fails for any
      // reason (sharp crash, bad image), the operator still lands on /queue
      // and can retry from /branding. The error path bubbles up to the UI
      // only if it failed before we flipped onboardingCompleted, so the
      // operator isn't stuck on the wizard.
      if (logoFile) {
        try {
          const path = logoSourcePath(tenantId, logoFile);
          await uploadFile(path, logoFile);
          await callBrandingRegenerate({ tenantId, sourcePath: path });
        } catch (e) {
          logger.error('wizard logo regenerate failed', { error: String(e) });
          // Soft-fail: continue to finish onboarding, surface a non-blocking
          // notice via the error state. (User can retry from /branding.)
          setSubmitError(t('dashboard.welcome.errLogoFailed'));
        }
      }

      // Flip onboardingCompleted on the tenant root doc
      await tenantsRepo.update(tenantId, {
        'profile.onboardingCompleted': true,
        'profile.updatedAt': now,
      });

      onDone();
    } catch (e) {
      logger.error('wizard commit failed', { error: String(e) });
      setSubmitError((e as Error).message ?? t('dashboard.welcome.errCouldNotSave'));
      setSubmitting(false);
    }
  }

  return (
    <main
      className="min-h-dvh flex flex-col items-center px-6 py-12"
      style={{ background: 'var(--color-surface)', color: 'var(--color-on-surface)' }}
    >
      <div style={{ width: '100%', maxWidth: 560 }}>
        <StepIndicator current={step} total={4} brandName={brandName} />

        {step === 1 ? (
          <Step1
            outletName={outletName}
            setOutletName={setOutletName}
            address={address}
            setAddress={setAddress}
            onNext={() => setStep(2)}
          />
        ) : null}

        {step === 2 ? (
          <Step2
            openDays={openDays}
            setOpenDays={setOpenDays}
            openTime={openTime}
            setOpenTime={setOpenTime}
            closeTime={closeTime}
            setCloseTime={setCloseTime}
            onBack={() => setStep(1)}
            onNext={() => setStep(3)}
          />
        ) : null}

        {step === 3 ? (
          <Step3Logo
            logoFile={logoFile}
            setLogoFile={setLogoFile}
            logoPreview={logoPreview}
            onBack={() => setStep(2)}
            onNext={() => setStep(4)}
          />
        ) : null}

        {step === 4 ? (
          <Step4
            outletName={outletName.trim()}
            hasLogo={logoFile !== null}
            onBack={() => setStep(3)}
            onFinish={() => void commitAndFinish()}
            submitting={submitting}
            error={submitError}
          />
        ) : null}
      </div>
    </main>
  );
}

function StepIndicator({
  current,
  total,
  brandName,
}: {
  readonly current: WizardStep;
  readonly total: number;
  readonly brandName: string;
}) {
  const { t } = useI18n();
  return (
    <div style={{ marginBottom: 'var(--space-8)' }}>
      <p
        style={{
          fontSize: 'var(--text-meta)',
          color: 'var(--color-on-surface-muted)',
          letterSpacing: 4,
          textTransform: 'uppercase',
          margin: 0,
        }}
      >
        {brandName} · {t('dashboard.welcome.setupEyebrow')}
      </p>
      <div
        style={{
          display: 'flex',
          gap: 6,
          marginTop: 'var(--space-3)',
        }}
      >
        {Array.from({ length: total }, (_, i) => i + 1).map((i) => (
          <span
            key={i}
            aria-hidden
            style={{
              flex: 1,
              height: 4,
              borderRadius: 'var(--radius-pill)',
              background: i <= current ? 'var(--color-primary)' : 'var(--color-border)',
              transition: 'background var(--duration-2) var(--ease-gentle)',
            }}
          />
        ))}
      </div>
      <p
        style={{
          fontSize: 'var(--text-meta)',
          color: 'var(--color-on-surface-muted)',
          margin: 'var(--space-2) 0 0 0',
        }}
      >
        {t('dashboard.welcome.stepOf', { current, total })}
      </p>
    </div>
  );
}

function Step1({
  outletName,
  setOutletName,
  address,
  setAddress,
  onNext,
}: {
  readonly outletName: string;
  readonly setOutletName: (s: string) => void;
  readonly address: string;
  readonly setAddress: (s: string) => void;
  readonly onNext: () => void;
}) {
  const { t } = useI18n();
  const canNext = outletName.trim().length > 0;
  function submit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    if (canNext) onNext();
  }
  return (
    <form onSubmit={submit} style={{ display: 'grid', gap: 'var(--space-6)' }}>
      <header>
        <h1 style={{ fontSize: 'var(--text-h1)', fontWeight: 600, margin: 0 }}>
          {t('dashboard.welcome.step1Title')}
        </h1>
        <p
          style={{
            fontSize: 'var(--text-meta)',
            color: 'var(--color-on-surface-muted)',
            marginTop: 'var(--space-2)',
          }}
        >
          {t('dashboard.welcome.step1Subtitle')}
        </p>
      </header>

      {/* The first thing a new operator ever types into this product. It was a
          bare input while the marketing signup form they arrived from uses the
          premium field — so the polish dropped at exactly the handover. */}
      <PremiumField
        label={t('dashboard.welcome.outletNameLabel')}
        icon="building"
        autoFocus
        required
        value={outletName}
        onChange={setOutletName}
        placeholder={t('dashboard.welcome.outletNamePlaceholder')}
        autoComplete="organization"
      />

      <label style={{ display: 'grid', gap: 'var(--space-2)' }}>
        <span style={fieldLabel}>{t('dashboard.welcome.addressLabel')}</span>
        <textarea
          value={address}
          onChange={(e) => setAddress(e.target.value)}
          placeholder={t('dashboard.welcome.addressPlaceholder')}
          rows={3}
          autoComplete="street-address"
          style={{ ...inputStyle, resize: 'vertical', fontFamily: 'inherit' }}
        />
      </label>

      <WizardActions
        primaryLabel={t('common.next')}
        primaryDisabled={!canNext}
        onPrimary={() => onNext()}
      />
    </form>
  );
}

function Step2({
  openDays,
  setOpenDays,
  openTime,
  setOpenTime,
  closeTime,
  setCloseTime,
  onBack,
  onNext,
}: {
  readonly openDays: Set<DayKey>;
  readonly setOpenDays: (next: Set<DayKey>) => void;
  readonly openTime: string;
  readonly setOpenTime: (s: string) => void;
  readonly closeTime: string;
  readonly setCloseTime: (s: string) => void;
  readonly onBack: () => void;
  readonly onNext: () => void;
}) {
  const { t } = useI18n();
  function toggle(d: DayKey) {
    const next = new Set(openDays);
    if (next.has(d)) next.delete(d);
    else next.add(d);
    setOpenDays(next);
  }
  return (
    <div style={{ display: 'grid', gap: 'var(--space-6)' }}>
      <header>
        <h1 style={{ fontSize: 'var(--text-h1)', fontWeight: 600, margin: 0 }}>
          {t('dashboard.welcome.step2Title')}
        </h1>
        <p
          style={{
            fontSize: 'var(--text-meta)',
            color: 'var(--color-on-surface-muted)',
            marginTop: 'var(--space-2)',
          }}
        >
          {t('dashboard.welcome.step2Subtitle')}
        </p>
      </header>

      <div>
        <p style={{ ...fieldLabel, marginBottom: 'var(--space-2)' }}>
          {t('dashboard.welcome.openDaysLabel')}
        </p>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 'var(--space-2)' }}>
          {DAYS.map((d) => {
            const on = openDays.has(d);
            return (
              <button
                key={d}
                type="button"
                aria-pressed={on}
                onClick={() => toggle(d)}
                style={{
                  padding: '10px 14px',
                  borderRadius: 'var(--radius-pill)',
                  border: `1px solid ${on ? 'var(--color-primary)' : 'var(--color-border-strong)'}`,
                  background: on ? 'var(--color-primary)' : 'transparent',
                  color: on ? 'var(--color-primary-fg)' : 'var(--color-on-surface)',
                  fontSize: 'var(--text-meta)',
                  fontWeight: 500,
                  cursor: 'pointer',
                  textTransform: 'capitalize',
                }}
              >
                {t(`dashboard.loyaltyPromotions.dow.${d}`)}
              </button>
            );
          })}
        </div>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 'var(--space-4)' }}>
        <label style={{ display: 'grid', gap: 'var(--space-2)' }}>
          <span style={fieldLabel}>{t('dashboard.welcome.openTimeLabel')}</span>
          <input
            type="time"
            value={openTime}
            onChange={(e) => setOpenTime(e.target.value)}
            style={inputStyle}
          />
        </label>
        <label style={{ display: 'grid', gap: 'var(--space-2)' }}>
          <span style={fieldLabel}>{t('dashboard.welcome.closeTimeLabel')}</span>
          <input
            type="time"
            value={closeTime}
            onChange={(e) => setCloseTime(e.target.value)}
            style={inputStyle}
          />
        </label>
      </div>

      <WizardActions
        secondaryLabel={t('common.back')}
        onSecondary={onBack}
        primaryLabel={t('common.next')}
        onPrimary={onNext}
      />
    </div>
  );
}

function Step3Logo({
  logoFile,
  setLogoFile,
  logoPreview,
  onBack,
  onNext,
}: {
  readonly logoFile: File | null;
  readonly setLogoFile: (f: File | null) => void;
  readonly logoPreview: string | null;
  readonly onBack: () => void;
  readonly onNext: () => void;
}) {
  const { t } = useI18n();
  const [fileError, setFileError] = useState<string | null>(null);

  function pick(e: React.ChangeEvent<HTMLInputElement>) {
    setFileError(null);
    const f = e.target.files?.[0] ?? null;
    if (!f) return;
    if (!/^image\/(png|jpeg|svg\+xml|webp)$/.test(f.type)) {
      setFileError(t('dashboard.welcome.errFileType'));
      return;
    }
    if (f.size > 5 * 1024 * 1024) {
      setFileError(t('dashboard.welcome.errFileSize'));
      return;
    }
    setLogoFile(f);
  }

  return (
    <div style={{ display: 'grid', gap: 'var(--space-6)' }}>
      <header>
        <h1 style={{ fontSize: 'var(--text-h1)', fontWeight: 600, margin: 0 }}>
          {t('dashboard.welcome.step3Title')}
        </h1>
        <p
          style={{
            fontSize: 'var(--text-meta)',
            color: 'var(--color-on-surface-muted)',
            marginTop: 'var(--space-2)',
          }}
        >
          {t('dashboard.welcome.step3Subtitle')}
        </p>
      </header>

      <div
        style={{
          display: 'grid',
          gridTemplateColumns: '128px 1fr',
          gap: 'var(--space-4)',
          alignItems: 'center',
        }}
      >
        <div
          aria-hidden
          style={{
            width: 128,
            height: 128,
            borderRadius: 'var(--radius-md)',
            border: '1px dashed var(--color-border-strong)',
            background: 'var(--tint-subtle)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            overflow: 'hidden',
          }}
        >
          {logoPreview ? (
            <img
              src={logoPreview}
              alt=""
              style={{ width: '100%', height: '100%', objectFit: 'contain' }}
            />
          ) : (
            <span style={{ fontSize: 'var(--text-meta)', color: 'var(--color-on-surface-muted)' }}>
              {t('dashboard.welcome.logoPreview')}
            </span>
          )}
        </div>
        <div style={{ display: 'grid', gap: 'var(--space-2)' }}>
          <label
            style={{
              ...primaryButtonStyleInline,
              display: 'inline-flex',
              cursor: 'pointer',
              width: 'fit-content',
            }}
          >
            {logoFile
              ? t('dashboard.welcome.chooseDifferentFile')
              : t('dashboard.welcome.chooseFile')}
            <input
              type="file"
              accept="image/png,image/jpeg,image/svg+xml,image/webp"
              onChange={pick}
              style={{ display: 'none' }}
            />
          </label>
          {logoFile ? (
            <button
              type="button"
              onClick={() => setLogoFile(null)}
              style={{
                background: 'transparent',
                border: 'none',
                color: 'var(--color-on-surface-muted)',
                fontSize: 'var(--text-meta)',
                textAlign: 'left',
                padding: 0,
                cursor: 'pointer',
                width: 'fit-content',
              }}
            >
              {t('dashboard.welcome.remove')}
            </button>
          ) : null}
          {fileError ? (
            <p
              role="alert"
              style={{
                color: 'var(--color-accent)',
                fontSize: 'var(--text-meta)',
                margin: 0,
              }}
            >
              {fileError}
            </p>
          ) : null}
        </div>
      </div>

      <WizardActions
        secondaryLabel={t('common.back')}
        onSecondary={onBack}
        primaryLabel={logoFile ? t('common.next') : t('dashboard.welcome.skipForNow')}
        onPrimary={onNext}
      />
    </div>
  );
}

function Step4({
  outletName,
  hasLogo,
  onBack,
  onFinish,
  submitting,
  error,
}: {
  readonly outletName: string;
  readonly hasLogo: boolean;
  readonly onBack: () => void;
  readonly onFinish: () => void;
  readonly submitting: boolean;
  readonly error: string | null;
}) {
  const { t } = useI18n();
  return (
    <div style={{ display: 'grid', gap: 'var(--space-6)' }}>
      <header>
        <h1 style={{ fontSize: 'var(--text-h1)', fontWeight: 600, margin: 0 }}>
          {t('dashboard.welcome.step4Title')}
        </h1>
        <p
          style={{
            fontSize: 'var(--text-meta)',
            color: 'var(--color-on-surface-muted)',
            marginTop: 'var(--space-2)',
          }}
        >
          {t('dashboard.welcome.step4Subtitle')}
        </p>
      </header>

      <div
        style={{
          padding: 'var(--space-4) var(--space-5)',
          borderRadius: 'var(--radius-lg)',
          background: 'rgba(13,122,79,0.06)',
          border: '1px solid rgba(13,122,79,0.18)',
        }}
      >
        <p style={{ margin: 0, fontWeight: 500 }}>
          {outletName || t('dashboard.welcome.yourOutletFallback')}
        </p>
        <p
          style={{
            fontSize: 'var(--text-meta)',
            color: 'var(--color-on-surface-muted)',
            margin: 'var(--space-1) 0 0 0',
          }}
        >
          {hasLogo ? t('dashboard.welcome.logoUploadedNote') : t('dashboard.welcome.noLogoNote')}
        </p>
      </div>

      {error ? (
        <p
          role="alert"
          style={{
            color: 'var(--color-accent)',
            fontSize: 'var(--text-meta)',
            margin: 0,
          }}
        >
          {error}
        </p>
      ) : null}

      <WizardActions
        secondaryLabel={t('common.back')}
        onSecondary={onBack}
        primaryLabel={
          submitting ? t('dashboard.welcome.saving') : t('dashboard.welcome.takeMeToQueue')
        }
        primaryDisabled={submitting}
        onPrimary={onFinish}
      />
    </div>
  );
}

function WizardActions({
  secondaryLabel,
  onSecondary,
  primaryLabel,
  onPrimary,
  primaryDisabled,
}: {
  readonly secondaryLabel?: string;
  readonly onSecondary?: () => void;
  readonly primaryLabel: string;
  readonly onPrimary: () => void;
  readonly primaryDisabled?: boolean;
}) {
  return (
    <div
      style={{
        display: 'flex',
        gap: 'var(--space-2)',
        marginTop: 'var(--space-2)',
        justifyContent: 'space-between',
      }}
    >
      {secondaryLabel ? (
        <button
          type="button"
          onClick={onSecondary}
          style={{
            background: 'transparent',
            color: 'var(--color-on-surface-muted)',
            padding: '12px 18px',
            border: '1px solid var(--color-border-strong)',
            borderRadius: 'var(--radius-md)',
            cursor: 'pointer',
            fontWeight: 500,
          }}
        >
          {secondaryLabel}
        </button>
      ) : (
        <span />
      )}
      <button
        type="button"
        disabled={primaryDisabled}
        onClick={onPrimary}
        style={{
          background: 'var(--color-primary)',
          color: 'var(--color-primary-fg)',
          padding: '12px 22px',
          border: 'none',
          borderRadius: 'var(--radius-md)',
          fontWeight: 500,
          fontSize: 'var(--text-body)',
          cursor: primaryDisabled ? 'not-allowed' : 'pointer',
          opacity: primaryDisabled ? 0.6 : 1,
          boxShadow: 'var(--shadow-2)',
        }}
      >
        {primaryLabel}
      </button>
    </div>
  );
}

// ── Post-onboarding summary (shown when /welcome is visited after completion) ──

function PostOnboardingSummary({
  brand,
  profile,
  subscription,
  onOpenQueue,
}: {
  readonly brand: {
    readonly name: string;
    readonly domains: { readonly marketing: string };
  };
  readonly profile: TenantProfile;
  readonly subscription: SubscriptionState | null;
  readonly onOpenQueue: () => void;
}) {
  const { t } = useI18n();
  const previewUrl = customerAppUrl(brand.domains, profile.slug);
  const trialMsLeft = useMemo(() => computeTrialMsLeft(subscription), [subscription]);

  return (
    <main
      className="min-h-dvh flex flex-col items-center px-6 py-12"
      style={{ background: 'var(--color-surface)', color: 'var(--color-on-surface)' }}
    >
      <div style={{ width: '100%', maxWidth: 560 }}>
        <p
          style={{
            fontSize: 'var(--text-meta)',
            color: 'var(--color-on-surface-muted)',
            letterSpacing: 4,
            textTransform: 'uppercase',
            margin: 0,
          }}
        >
          {brand.name} · {profile.status}
        </p>
        <h1 style={{ fontSize: 'var(--text-h1)', fontWeight: 600, marginTop: 'var(--space-3)' }}>
          {t('dashboard.welcome.summaryLive', { name: profile.businessName })}
        </h1>
        <p
          style={{
            fontSize: 'var(--text-body)',
            color: 'var(--color-on-surface-muted)',
            marginTop: 'var(--space-2)',
          }}
        >
          {t('dashboard.welcome.summaryReadyAtPre')}{' '}
          <a
            href={previewUrl}
            target="_blank"
            rel="noreferrer"
            style={{ color: 'var(--color-primary)' }}
          >
            {customerAppUrlForDisplay(brand.domains, profile.slug)}
          </a>
          .
        </p>

        {trialMsLeft !== null ? (
          <p
            style={{
              fontSize: 'var(--text-meta)',
              color: 'var(--color-on-surface-muted)',
              marginTop: 'var(--space-4)',
            }}
          >
            {t('dashboard.welcome.trialEndsIn', { time: formatDaysLeft(trialMsLeft, t) })}
          </p>
        ) : null}

        <div
          style={{
            marginTop: 'var(--space-6)',
            display: 'flex',
            gap: 'var(--space-2)',
            flexWrap: 'wrap',
          }}
        >
          <button
            type="button"
            onClick={onOpenQueue}
            style={{
              background: 'var(--color-primary)',
              color: 'var(--color-primary-fg)',
              padding: '12px 22px',
              border: 'none',
              borderRadius: 'var(--radius-md)',
              fontWeight: 500,
              cursor: 'pointer',
              boxShadow: 'var(--shadow-2)',
            }}
          >
            {t('dashboard.welcome.openQueue')}
          </button>
          <Link
            to="/qr-print"
            style={{
              padding: '12px 22px',
              borderRadius: 'var(--radius-md)',
              border: '1px solid var(--color-border-strong)',
              color: 'var(--color-on-surface)',
              textDecoration: 'none',
              fontWeight: 500,
              display: 'inline-flex',
              alignItems: 'center',
            }}
          >
            {t('dashboard.welcome.printYourQr')}
          </Link>
        </div>
      </div>
    </main>
  );
}

// ── styles + helpers ────────────────────────────────────────────────────

const fieldLabel: React.CSSProperties = {
  fontSize: 'var(--text-meta)',
  color: 'var(--color-on-surface-muted)',
  fontWeight: 500,
};

const primaryButtonStyleInline: React.CSSProperties = {
  background: 'var(--color-primary)',
  color: 'var(--color-primary-fg)',
  padding: '10px 16px',
  borderRadius: 'var(--radius-md)',
  border: 'none',
  fontWeight: 500,
  fontSize: 'var(--text-meta)',
  boxShadow: 'var(--shadow-1)',
};

function Screen({ body }: { readonly body: string }) {
  return (
    <main className="min-h-dvh flex items-center justify-center p-8 text-center">
      <p style={{ fontSize: 'var(--text-meta)', color: 'var(--color-on-surface-muted)' }}>{body}</p>
    </main>
  );
}

function computeTrialMsLeft(sub: SubscriptionState | null): number | null {
  if (!sub) return null;
  if (sub.status !== 'trial') return null;
  const endsAt = sub.trialEndsAt;
  if (!endsAt) return null;
  const endMs = typeof endsAt.toMillis === 'function' ? endsAt.toMillis() : endsAt.seconds * 1000;
  return Math.max(0, endMs - Date.now());
}

function formatDaysLeft(
  ms: number,
  t: (key: string, vars?: Record<string, string | number>) => string,
): string {
  const days = Math.max(0, Math.round(ms / (24 * 60 * 60 * 1000)));
  return t(days === 1 ? 'dashboard.welcome.daysOne' : 'dashboard.welcome.daysOther', {
    count: days,
  });
}
